import React from 'react'
import { useTranslation } from 'react-i18next'
import { OnFieldChangeType, Styles } from '../../../../@types/types'
import { formatNumberForCalc, formatNumberForDisplay } from '../../../../i18next/decimal'
import { PriceFieldType } from '../../../pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import CalculationBoxEditor from './CalculationBoxEditor'
import { DefaultRentPriceTypeOption } from '../../../../@types/dropdown-types'
import { ScreenName } from '../../../../services/domain/wizardScreens'

const styles: Styles = {
  totalRent: {
    borderTop: '1px solid #eaeaea',
    paddingTop: 12,
  },
}

export interface CostCalculationBoxProps {
  screenName: ScreenName
  priceValue?: number
  additionalCostsValue?: number
  heatingCostsValue?: string
  priceFieldType: PriceFieldType
  priceType?: DefaultRentPriceTypeOption
  enableEditTotalRent?: boolean
  manualTotalRent?: string
  onChange: OnFieldChangeType
}

const CostCalculationBox: React.FC<CostCalculationBoxProps> = ({
  screenName,
  priceValue,
  additionalCostsValue,
  heatingCostsValue,
  priceFieldType,
  priceType,
  manualTotalRent,
  enableEditTotalRent = false,
  onChange,
}) => {
  const price = priceValue ?? 0
  const additionalCosts = additionalCostsValue ?? 0
  const heatingCosts = formatNumberForCalc(heatingCostsValue)
  const total = heatingCostsValue ? price + additionalCosts + heatingCosts : price + additionalCosts
  const { t } = useTranslation()
  const unit = priceType === DefaultRentPriceTypeOption.RENT_PER_SQM_PRICE ? '€/m²' : '€'
  const totalRentSpan = <>{`${formatNumberForDisplay(total)} ${unit}`}</>
  return (
    <div id='cost-calculation-box'>
      <div className='padding-bottom-s'>
        <label className='lap-seven-twelfths desk-seven-twelfths'>
          {t(
            `wizard.screen.costs.costCalculation.${
              priceFieldType === PriceFieldType.COLD_RENT ? 'coldRent' : 'rentPrice'
            }`,
            'Rent'
          )}
        </label>
        <span
          className={`lap-five-twelfths desk-five-twelfths float-right font-nowrap align-right${
            enableEditTotalRent ? ' font-ellipsis' : ''
          }`}
        >
          {`${formatNumberForDisplay(price)} ${unit}`}
        </span>
      </div>
      <div className='padding-bottom-s'>
        <label className='slap-seven-twelfths desk-seven-twelfths'>
          {t('wizard.screen.costs.costCalculation.additionalCosts', 'Additional Costs')}
        </label>
        <span
          className={`lap-five-twelfths desk-five-twelfths float-right font-nowrap align-right${
            enableEditTotalRent ? ' font-ellipsis' : ''
          }`}
        >{`${formatNumberForDisplay(additionalCosts)} ${unit}`}</span>
      </div>
      {heatingCostsValue !== undefined && (
        <div className='padding-bottom-s'>
          <label className='slap-seven-twelfths desk-seven-twelfths'>
            {t('wizard.screen.costs.costCalculation.heatingCosts', 'Heating Costs')}
          </label>
          <span
            className={`lap-five-twelfths desk-five-twelfths float-right font-nowrap align-right${
              enableEditTotalRent ? ' font-ellipsis' : ''
            }`}
          >{`${formatNumberForDisplay(heatingCosts)} ${unit}`}</span>
        </div>
      )}
      <div className='font-body-medium-bold' style={styles.totalRent}>
        <label className='lap-seven-twelfths desk-seven-twelfths margin-top-xs'>
          {t('wizard.screen.costs.costCalculation.totalRent', 'Total Rent')}
        </label>
        {enableEditTotalRent ? (
          <CalculationBoxEditor
            screenName={screenName}
            calcTotalRent={total}
            manualTotalRent={manualTotalRent}
            totalRentSpan={totalRentSpan}
            onChange={onChange}
          />
        ) : (
          <span className='lap-five-twelfths desk-five-twelfths float-right font-nowrap align-right'>
            {totalRentSpan}
          </span>
        )}
      </div>
    </div>
  )
}

export default CostCalculationBox
